import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_ICON = { pending: <Clock size={14} className="text-accent" />, approved: <CheckCircle2 size={14} className="text-primary" />, rejected: <XCircle size={14} className="text-destructive" /> };

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/pending-payments").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/pending-payments/${id}/approve`);
      toast.success("Payment approved — student unlocked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this payment request?")) return;
    setBusy(id);
    try {
      await api.post(`/admin/pending-payments/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };

  const pending = items.filter(i => i.status === "pending");
  const done = items.filter(i => i.status !== "pending");

  const methodBadge = (method) => {
    const m = method || "cashapp";
    const label = m === "paypal" ? "PayPal" : "Cash App";
    const cls = m === "paypal"
      ? "bg-[#0070ba]/10 text-[#0070ba] border-[#0070ba]/30"
      : "bg-emerald-500/10 text-emerald-600 border-emerald-500/30";
    return (
      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
        {label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3 flex items-center gap-2"><Wallet size={14} /> PayPal · Cash App queue</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">Pending payments.</h1>

        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-3">Awaiting review ({pending.length})</h2>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments waiting. Nice work.</p>
          ) : (
            <div className="space-y-3" data-testid="pending-payments-list">
              {pending.map(p => (
                <Card key={p.id} className="p-5" data-testid={`payment-row-${p.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="label-caps mb-1 flex items-center gap-2">{STATUS_ICON.pending} Pending · {p.plan} {methodBadge(p.method)}</div>
                      <div className="font-serif text-2xl">{p.user_name}</div>
                      <div className="text-xs text-muted-foreground">{p.user_email} · ${p.amount.toFixed(2)}</div>
                      {p.note && <div className="text-sm mt-2 italic border-l-2 border-border pl-3">"{p.note}"</div>}
                      <div className="text-[10px] text-muted-foreground mt-2">{new Date(p.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => reject(p.id)} disabled={busy === p.id} data-testid={`reject-payment-${p.id}`}>Reject</Button>
                      <Button size="sm" onClick={() => approve(p.id)} disabled={busy === p.id} data-testid={`approve-payment-${p.id}`}>Approve & unlock</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">History ({done.length})</h2>
          {done.length === 0 ? (
            <p className="text-sm text-muted-foreground">No history yet.</p>
          ) : (
            <div className="space-y-2 opacity-80">
              {done.map(p => (
                <div key={p.id} className="p-3 border border-border rounded-md text-sm flex items-center justify-between" data-testid={`payment-history-${p.id}`}>
                  <span className="flex items-center gap-2">{STATUS_ICON[p.status]} {methodBadge(p.method)} <strong>{p.user_name}</strong> — {p.plan} — ${p.amount.toFixed(2)}</span>
                  <span className="text-xs text-muted-foreground">{new Date(p.approved_at || p.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
